import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useLocation, useRouteMatch } from 'react-router';
import sortBy from 'lodash/sortBy';
import { t } from '@lingui/core/macro';

import { AuthContext, AuthContextValue } from './AuthContext';
import UWEContext, { WorkpageDef } from './UWEContext';
import Jnx from '../util/jnx';
import useSearchParams from '../util/useSearchParams';
import { getLabel } from '../LanguageConstant';
import { useLingui } from '@lingui/react/macro';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ActiveEntity {
  id: string | number;
  [key: string]: unknown;
}

export type WorkpageMatch = WorkpageDef & {
  slugMatch?: boolean;
  exprMatch?: boolean;
};

export interface BreadcrumbItem {
  label?: string;
  icon?: string;
  to?: string;
}

export interface RouterContextValue {
  activeEntity: ActiveEntity | null;
  setActiveEntity: React.Dispatch<React.SetStateAction<ActiveEntity | null>>;
  navTitle: string | null;
  setNavTitle: React.Dispatch<React.SetStateAction<string | null>>;
  workpageMatch: WorkpageMatch | undefined;
  sidebarTab: string | undefined;
  isSettingsActive: boolean;
  isSystemSettingsRoute: boolean;
  isFaqActive: boolean;
  navBreadcrumbs: BreadcrumbItem[];
}

// ── Context ────────────────────────────────────────────────────────────────────

const RouterContext = React.createContext<RouterContextValue | undefined>(undefined);

export function RouterContextProvider({ children }: { children: React.ReactNode }) {
  const value = useRouterContextHooks();
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouterContext(): RouterContextValue | {} {
  const ctx = useContext(RouterContext);
  if (!ctx) {
    console.error('useRouterContext must be used within RouterContextProvider');
    return {};
  }
  return ctx;
}

// ── Internal hook ──────────────────────────────────────────────────────────────

function useRouterContextHooks(): RouterContextValue {
  const { i18n } = useLingui();

  const labels = useMemo(() => {
    return getLabel();
  }, [i18n.locale]);

  const auth = useContext(AuthContext) as AuthContextValue;
  const uweCtx = useContext(UWEContext);
  const workpages = uweCtx?.workpages ?? [];
  const adminWorkpages = uweCtx?.adminWorkpages ?? [];
  const { pathname } = useLocation();
  const {
    params: { slug, entityType, setting, id },
  } = useRouteMatch<{ slug?: string; entityType?: string; setting?: string; id?: string }>();
  const searchParams = useSearchParams({});

  // Active entity
  const [activeEntity, setActiveEntity] = useState<ActiveEntity | null>(null);

  useEffect(() => {
    if (activeEntity == null || !activeEntity.id) return;
    const observer = new MutationObserver(() => {
      if (document.getElementById(`entity-form-${activeEntity.id}`) == null) {
        setActiveEntity(null);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [activeEntity]);

  // Nav title
  const [navTitle, setNavTitle] = useState<string | null>(null);

  // Workpage match
  const workpageMatch = useMemo<WorkpageMatch | undefined>(() => {
    const allWorkpages = [...sortBy(workpages ?? [], wp => wp.order), ...sortBy(adminWorkpages ?? [], wp => wp.order)];
    const tabSlug = slug ?? setting ?? entityType;
    const matches = allWorkpages.reduce<WorkpageMatch[]>((result, wp) => {
      if (wp.slug === tabSlug) return [...result, { ...wp, slugMatch: true }];

      const matchSidebar = (wp.definition as { matchSidebarExpr?: string | string[] } | undefined)?.matchSidebarExpr;
      if (matchSidebar) {
        const expr = Array.isArray(matchSidebar) ? matchSidebar.join(' ') : matchSidebar;
        try {
          const doesMatch = (new Jnx(expr) as { eval: (...args: unknown[]) => unknown }).eval(auth ?? {}, '', { activeEntity, slug: tabSlug, auth });
          if (doesMatch) return [...result, { ...wp, exprMatch: true }];
        } catch (_) {}
      }
      return result;
    }, []);

    return sortBy(matches, [m => m.slugMatch])[0];
  }, [workpages, adminWorkpages, slug, entityType, setting, activeEntity]);

  // Sidebar tab (the selected key)
  const sidebarTab = useMemo(() => workpageMatch?.slug ?? slug ?? setting ?? entityType, [workpageMatch, slug, entityType, setting]);

  const isSettingsActive = pathname === '/system-settings';
  const isSystemSettingsRoute = pathname.startsWith('/system-settings');
  const isFaqActive = pathname === '/faq';

  const searchParamKeys = useMemo(() => {
    return Object.keys(searchParams?.[0]?.[0] || {});
  }, [searchParams]);

  const navBreadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const crumbs: BreadcrumbItem[] = [{ icon: 'phosphor-HouseDuotone', to: '/home' }];

    if (pathname === '/system-settings') {
      crumbs.push({ label: t`Settings` });
      return crumbs;
    }

    // Handle ADMIN breadcrumbs
    if (pathname.startsWith('/system-settings') || workpageMatch?.isAdmin) {
      crumbs.push({ label: t`Settings`, to: '/system-settings' });

      if (workpageMatch && !pathname.endsWith(`/${workpageMatch.slug}`)) {
        crumbs.push({ label: workpageMatch.name, to: `/system-settings/${workpageMatch.slug}` });
      }

      // A tab whose own record is addressed by a URL segment (e.g.
      // /system-settings/define-inventory-schedules/<fireId>), rather than by a query-string param like
      // the switch below. The record's own id is the useful last crumb here — falling through to
      // `navTitle` would just repeat the workpage's name a second time, since that's what the generic
      // Workpage wrapper sets it to regardless of which record is open.
      if (id) {
        crumbs.push({ label: id === 'new' ? t`New` : id });
        return crumbs;
      }

      if (searchParamKeys.length > 0) {
        switch (true) {
          case searchParamKeys.includes('user'):
            crumbs.push({ label: labels.users, to: '/system-settings/users' });
            break;
          case searchParamKeys.includes('orgUnit'):
            crumbs.push({ label: labels.organizationalUnits, to: '/system-settings/organizational-units' });
            break;
          case searchParamKeys.includes('institution'):
            crumbs.push({ label: labels.institutions, to: '/system-settings/institutions' });
            break;
          case searchParamKeys.includes('orgUnitType'):
            crumbs.push({ label: labels.organizationalUnitTypes, to: '/system-settings/organizational-unit-types' });
            break;
          case searchParamKeys.includes('stepName') && pathname.includes('workflow-notifications'):
            crumbs.push({ label: labels.workflowNotifications, to: '/system-settings/workflow-notifications' });
            break;
          case searchParamKeys.includes('notification'):
            crumbs.push({ label: labels.popupNotifications, to: '/system-settings/notification' });
            break;
          case searchParamKeys.includes('workflow'):
            crumbs.push({ label: labels.workflow, to: '/system-settings/workflow' });
            break;
          case searchParamKeys.includes('role'):
            crumbs.push({ label: labels.roles, to: '/system-settings/roles' });
            break;
          case searchParamKeys.includes('objectSchema'):
            crumbs.push({ label: labels.objectTypes, to: '/system-settings/object-schemas' });
            break;
          case searchParamKeys.includes('form') || searchParamKeys.includes('showTags') || searchParamKeys.includes('formTag'):
            crumbs.push({ label: labels.forms, to: '/system-settings/forms' });
            break;
          case searchParamKeys.includes('formTag'):
            crumbs.push({ label: labels.formTags, to: '/system-settings/forms?showTags=true' });
            break;
          case searchParamKeys.includes('workpage'):
            crumbs.push({ label: labels.workpages, to: '/system-settings/workpage' });
            break;
          case searchParamKeys.includes('integration'):
            crumbs.push({ label: labels.integrations, to: '/system-settings/integrations' });
            break;
          case searchParamKeys.includes('list'):
            crumbs.push({ label: labels.lists, to: '/system-settings/lists' });
            break;
          case searchParamKeys.includes('sequence'):
            crumbs.push({ label: labels.sequences, to: '/system-settings/sequences' });
            break;
          case searchParamKeys.includes('id') && pathname.includes('document-templates'): // TODO document templates
            crumbs.push({ label: labels.documentTemplates, to: '/system-settings/document-templates' });
            break;
          case searchParamKeys.includes('themeId'):
            crumbs.push({ label: labels.themes, to: '/system-settings/themeEditor' });
            break;

          case searchParamKeys.includes('faq'):
            crumbs.push({ label: labels.faq, to: '/system-settings/faq' });
            break;
          default:
        }
      }
      if (navTitle) crumbs.push({ label: navTitle });
      return crumbs;
    }

    if (workpageMatch) {
      if (!pathname.endsWith('/tray')) {
        crumbs.push({ label: workpageMatch.name, to: `/workflow/${workpageMatch.slug}/tray` });
      }
    }

    if (navTitle) crumbs.push({ label: navTitle });

    return crumbs;
  }, [pathname, workpages, adminWorkpages, navTitle, workpageMatch, searchParamKeys, id]);

  return {
    activeEntity,
    setActiveEntity,
    navTitle,
    setNavTitle,
    workpageMatch,
    sidebarTab,
    isSettingsActive,
    isSystemSettingsRoute,
    isFaqActive,
    navBreadcrumbs,
  };
}

export default RouterContext;
